// src/components/SalaryInput.jsx
import React, { useState, useEffect } from 'react';
import { Box, TextField, Slider, Typography, useMediaQuery } from '@mui/material';
import { lightColours } from '../../../../styles/colours';
import { trackEvent } from '../../../../analytics';

const labels = ["£0", "£50,000", "£100,000", "£150,000", "£200,000"];

// Helper functions for formatting and unformatting
function unformat(value) {
  return value.replace(/[£,]/g, '');
}

function formatSalary(value) {
  const numberValue = Number(value);
  if (isNaN(numberValue)) return '';
  return '£' + numberValue.toLocaleString('en-UK', { maximumFractionDigits: 0 });
}

const SalaryInput = ({ salary, onSalaryChange }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  // Use local state for the text field's display value.
  const [inputValue, setInputValue] = useState(salary === 0 ? '' : formatSalary(salary));

  // Update local state if the external salary prop changes.
  useEffect(() => {
    setInputValue(salary === 0 ? '' : formatSalary(salary));
  }, [salary]);

  const handleSliderChange = (event, newValue) => {
    onSalaryChange(newValue);
  };

  const handleSliderCommit = (event, newValue) => {
    trackSalarySubmission(newValue);
  };

  const handleTextFieldChange = (event) => {
    const rawValue = event.target.value;
    // Remove the formatting characters
    const numericValueStr = unformat(rawValue);
    let numericValue = Number(numericValueStr);
    if (isNaN(numericValue)) {
      numericValue = 0;
    }
    // Constrain the value within bounds
    if (numericValue > 200000) numericValue = 200000;
    if (numericValue < 1) numericValue = 1;
    setInputValue(rawValue);
    onSalaryChange(numericValue);
  };

  const handleTextFieldBlur = () => {
    trackSalarySubmission(salary);
    setInputValue(formatSalary(salary));
  };

  const handleTextFieldFocus = () => {
    setInputValue(unformat(inputValue));
  };

  // Function to track salary submission
  const trackSalarySubmission = (value) => {
    const salaryRanges = [
      { min: 0, max: 20000, label: "<20k" },
      { min: 20000, max: 40000, label: "20k-40k" },
      { min: 40000, max: 60000, label: "40k-60k" },
      { min: 60000, max: 80000, label: "60k-80k" },
      { min: 80000, max: 100000, label: "80k-100k" },
      { min: 100000, max: 150000, label: "100k-150k" },
      { min: 150000, max: 200000, label: "150k+" }
    ];

    const salaryRange = salaryRanges.find(range => value >= range.min && value <= range.max)?.label || "Unknown";

    trackEvent("UK Take Home Pay Calculator", "Salary Submitted", salaryRange);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        width: "100%"
      }}
    >
      <TextField
        label="Salary"
        variant="outlined"
        // Use the local inputValue for display
        value={inputValue}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        onFocus={handleTextFieldFocus}
        fullWidth
        type="text"
        inputProps={{ inputMode: 'numeric' }}
        sx={{
          width: isMobile ? "90%" : "40%",
          height: "80px",
          "& .MuiInputBase-root": { height: "100%", padding: "12px 14px" },
          "& .MuiInputBase-input": { fontSize: "1.7rem", textAlign: "center", padding: "8px 12px" },
          mb: 2,
        }}
      />
      <Box sx={{ width: "100%", textAlign: "center" }}>
        <Slider
          value={salary}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit} // Fires only when user stops adjusting
          min={1}
          max={200000}
          step={1}
          sx={{
            width: "100%",
            color: lightColours.darkBlue,
            "& .MuiSlider-root": {
              cursor: "pointer",
            },
            "& .MuiSlider-thumb": {
              backgroundColor: lightColours.darkBlue,
              border: "2px solid",
              borderColor: lightColours.darkBlue,
              cursor: "grab",
              "&:active": {
                cursor: "grabbing",
              },
            },
            "& .MuiSlider-track": { 
              backgroundColor: lightColours.darkBlue,
              cursor: "pointer",
            },
            "& .MuiSlider-rail": { 
              backgroundColor: lightColours.darkBlue,
              cursor: "pointer",
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0 }}>
          {labels.map((label, index) => (
            <Typography key={index} sx={{ fontSize: "0.8rem", pl: 4 }}>{label}</Typography>
          ))}
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Note: This calculator is designed for annual salaries up to £200,000.
        </Typography>
      </Box>
    </Box>
  );
};

export default SalaryInput;
